import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import ProductionTableCard from './ProductionTableCard';
import { dayKeyDR } from '../utils/formatDate';

ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const DAYS_BACK = 30;

// Chart.js draws to a <canvas>, which doesn't resolve CSS custom properties —
// passing "var(--mm-cyan)" as a color renders black. Use the same literal hex
// values as the app's other charts (e.g. ProductionHoursBarChart.js).
const CYAN = '#12B8B2';
const CYAN_DIM = 'rgba(18,184,178,0.14)';

// activityLog and fillings are already fetched/held by adminDashboard.js — no new requests here.
const StaffActivityPanel = ({ activityLog = [], fillings = [] }) => {
  const perUser = useMemo(() => {
    const counts = {};
    activityLog.forEach((log) => {
      if (!log.user) return;
      counts[log.user] = (counts[log.user] || 0) + 1;
    });
    return { counts };
  }, [activityLog]);

  const topUsersChart = useMemo(() => {
    const sorted = Object.entries(perUser.counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return {
      labels: sorted.map(([user]) => user),
      datasets: [{ label: 'Actividades', data: sorted.map(([, count]) => count), backgroundColor: CYAN }],
    };
  }, [perUser]);

  const dailyVolumeChart = useMemo(() => {
    const byDay = {};
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - DAYS_BACK);

    activityLog.forEach((log) => {
      const ts = new Date(log.timestamp);
      if (ts < cutoff) return;
      const key = dayKeyDR(ts); // the Dominican day (toISOString is UTC: tomorrow after 8 p.m.)
      byDay[key] = (byDay[key] || 0) + 1;
    });

    const labels = Object.keys(byDay).sort();
    return {
      labels,
      datasets: [{ label: 'Actividades por Día', data: labels.map((d) => byDay[d]), borderColor: CYAN, backgroundColor: CYAN_DIM }],
    };
  }, [activityLog]);

  return (
    <div className="row mb-4">
      <div className="col-md-6">
        <Card className="mb-4 h-100">
          <Card.Body>
            <Card.Title>Actividad por Usuario</Card.Title>
            {topUsersChart.labels.length > 0 ? (
              <Bar data={topUsersChart} options={{ plugins: { legend: { display: false } } }} />
            ) : (
              <div className="text-muted">Sin actividad registrada.</div>
            )}
            <hr />
            <Card.Title style={{ fontSize: '1rem' }}>Volumen Diario (Últimos {DAYS_BACK} Días)</Card.Title>
            {dailyVolumeChart.labels.length > 0 ? (
              <Line data={dailyVolumeChart} options={{ plugins: { legend: { display: false } } }} />
            ) : (
              <div className="text-muted">Sin actividad reciente.</div>
            )}
          </Card.Body>
        </Card>
      </div>

      <div className="col-md-6">
        <ProductionTableCard fillings={fillings} />
      </div>
    </div>
  );
};

export default StaffActivityPanel;
